import store from '@/vuex/store'
import Sentry, { featureTags } from '@/services/sentry'

// PRRW-255: a flag gate reports a failure of the code it gates to Sentry under that one flag,
// so Sentry issues can be counted per Unleash flag without a hand-written capture per change.

// Flag keys carry their ticket id (`AHA-123-new-leaderboard`, `release-AHA-123-x`), as the flag-task-id check asks.
const TICKET_ID = /[A-Z][A-Z0-9]*-\d+/

export const flagContext = (flag, featureName) => {
  const taskId = flag.match(TICKET_ID)?.[0]
  const nameFromKey = taskId
    ? flag.replace(taskId, '').replace(/-{2,}/g, '-').replace(/^-|-$/g, '')
    : flag
  return { featureFlag: flag, featureName: featureName || nameFromKey, taskId }
}

export const captureFlagError = (error, flag, featureName) => {
  const { featureFlag, taskId, featureName: name } = flagContext(flag, featureName)
  return Sentry.captureException(error, {
    tags: featureTags({ featureFlag, featureName: name, taskId }),
  })
}

/**
 * Runs `newFn` when `flag` is on, `oldFn` (if given) when it is off.
 * A sync throw or a rejected promise from `newFn` is captured under the flag, then rethrown,
 * or answered by `oldFn` when `fallbackOnError` is set. Fallback is opt-in because `newFn`
 * may have written something before it failed, and running `oldFn` after it would write twice.
 * Sentry skips an error it already captured, so the rethrow is not reported again untagged.
 */
export const runFlagged = (flag, newFn, oldFn, { featureName, fallbackOnError = false } = {}) => {
  if (!store.getters['featureToggles/isFeatureEnabled'](flag)) return oldFn?.()

  const onError = (error) => {
    captureFlagError(error, flag, featureName)
    if (fallbackOnError && oldFn) return oldFn()
    throw error
  }

  let result
  try {
    result = newFn()
  } catch (error) {
    return onError(error)
  }
  return typeof result?.then === 'function' ? result.then(undefined, onError) : result
}
